import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Turnstile from '../components/Turnstile';
import './RegisterPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 10)             score++;
  if (pw.length >= 14)             score++;
  if (/[A-Z]/.test(pw))           score++;
  if (/[0-9]/.test(pw))           score++;
  if (/[^a-zA-Z0-9]/.test(pw))   score++;
  if (score <= 1) return { score, label: 'Weak',      color: '#e74c3c' };
  if (score <= 2) return { score, label: 'Fair',      color: '#e8954a' };
  if (score <= 3) return { score, label: 'Good',      color: '#c8a45a' };
  if (score <= 4) return { score, label: 'Strong',    color: '#2ecc71' };
  return               { score, label: 'Very Strong', color: '#27ae60' };
}

export default function RegisterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [username,   setUsername]   = useState('');
  const [email,      setEmail]      = useState('');
  const [dobDisplay, setDobDisplay] = useState(''); // what user sees
  const [dob,        setDob]        = useState(''); // YYYY-MM-DD sent to server
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [loading,    setLoading]    = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const [usernameFeedback, setUsernameFeedback] = useState<{available: boolean; reason: string} | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { if (user) navigate('/'); }, [user]);

  // Debounced username check — runs in background, never steals focus
  useEffect(() => {
    if (!username || username.length < 6) {
      setUsernameFeedback(null); return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const res = await fetch(`${API}/auth/check-username?username=${encodeURIComponent(username)}`);
        const data = await res.json();
        setUsernameFeedback(data);
      } catch {
        setUsernameFeedback(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 800);
  }, [username]);

  const strength = getPasswordStrength(password);

  // DOB: display stays as typed, dob state holds converted YYYY-MM-DD for server
  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDobDisplay(v);
    // Try to parse as DD/MM/YYYY for the server value
    const ddmmyyyy = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const [, d, m, y] = ddmmyyyy;
      setDob(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
    } else {
      setDob(v); // pass through as-is (handles YYYY-MM-DD direct entry)
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (usernameFeedback && !usernameFeedback.available) {
      setError('Please choose a different username.'); return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.'); return;
    }
    if (strength.score < 3) {
      setError('Please choose a stronger password.'); return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName, lastName, username, email,
          dateOfBirth: dob, password, turnstileToken
        }),
      });
      const data = await res.json();
      if (res.ok) {
        navigate('/verify-email', { state: { email } });
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch {
      setError('Could not connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">

      {/* ── LEFT: Hero ── */}
      <div className="register-hero">
        <img src="/guardian-bazaar-logo-dark.png" alt="Guardian Bazaar" className="register-hero-logo" />
        <div className="register-hero-tagline">
          Your personal Magic: The Gathering<br/>card collection manager
        </div>
      </div>

      {/* ── RIGHT: Form ── */}
      <div className="register-form-panel">
        <div className="register-greeting">
          <h1 className="register-title">Create an account</h1>
          <p className="register-subtitle">Join Guardian Bazaar to manage your collection</p>
        </div>

        <form className="register-form" onSubmit={handleRegister}>

          <div className="register-row">
            <div className="register-field">
              <label>First Name</label>
              <input type="text" value={firstName}
                     onChange={e => setFirstName(e.target.value)}
                     placeholder="First name" autoComplete="given-name" required />
            </div>
            <div className="register-field">
              <label>Last Name</label>
              <input type="text" value={lastName}
                     onChange={e => setLastName(e.target.value)}
                     placeholder="Last name" autoComplete="family-name" required />
            </div>
          </div>

          <div className="register-field">
            <label>Username <span className="register-hint">6–20 characters, letters/numbers/underscore</span></label>
            <div className="register-input-wrap">
              <input type="text" value={username}
                     onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                     placeholder="Choose a username"
                     minLength={6} maxLength={20}
                     autoComplete="username" required />
              {checkingUsername && (
                <span className="register-check-icon checking" aria-hidden="true">⟳</span>
              )}
              {!checkingUsername && usernameFeedback && (
                <span className={`register-check-icon ${usernameFeedback.available ? 'ok' : 'fail'}`} aria-hidden="true">
                  {usernameFeedback.available ? '✓' : '✗'}
                </span>
              )}
            </div>
            {usernameFeedback && !usernameFeedback.available && (
              <span className="register-field-error">{usernameFeedback.reason}</span>
            )}
            {usernameFeedback?.available && (
              <span className="register-field-ok">Username is available</span>
            )}
          </div>

          {/* Email — no tabIndex tricks needed; pointer-events:none on icons prevents focus steal */}
          <div className="register-field">
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              autoComplete="email"
              required />
          </div>

          <div className="register-field">
            <label>Date of Birth <span className="register-hint">DD/MM/YYYY</span></label>
            <input
              type="text"
              value={dobDisplay}
              onChange={handleDobChange}
              placeholder="DD/MM/YYYY"
              autoComplete="bday"
              required />
          </div>

          <div className="register-field">
            <label>Password <span className="register-hint">min 10 chars, uppercase, number, symbol</span></label>
            <input type="password" value={password}
                   onChange={e => setPassword(e.target.value)}
                   placeholder="Choose a strong password"
                   autoComplete="new-password" required />
            {password && (
              <div className="register-strength">
                <div className="register-strength-bar">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="register-strength-segment"
                         style={{ background: i <= strength.score ? strength.color : 'rgba(255,255,255,0.1)' }} />
                  ))}
                </div>
                <span className="register-strength-label" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          <div className="register-field">
            <label>Confirm Password</label>
            <div className="register-input-wrap">
              <input type="password" value={confirm}
                     onChange={e => setConfirm(e.target.value)}
                     placeholder="Repeat your password"
                     autoComplete="new-password" required />
              {confirm && (
                <span className={`register-check-icon ${confirm === password ? 'ok' : 'fail'}`} aria-hidden="true">
                  {confirm === password ? '✓' : '✗'}
                </span>
              )}
            </div>
          </div>

          {error   && <div className="register-error">{error}</div>}
          {success && <div className="register-success">{success}</div>}

          <Turnstile
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken('')}
            onError={() => setTurnstileToken('')}
          />

          <button type="submit" className="register-btn" disabled={loading || !turnstileToken}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>

          <div className="register-signin">
            Already have an account? <a href="/login" className="register-link">Sign in</a>
          </div>

        </form>
      </div>
    </div>
  );
}
